import React from "react";
import { RadioGroup, ToolTip } from "@truevault/aquilo";
import clsx from "clsx";
import { SurveyAnswers } from "../../../../common/hooks/api/useSurvey";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { CollectionContext } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UpdateVendorArgs } from "../components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import {
  hasGoogleAnalyticsDataSharingAnswer,
  isDataSellingLockedForVendor,
  isUploadLockedForVendor,
  shouldShowSelling,
} from "./vendorInventoryUtils";
import type { VendorInventoryItem } from "./vendorInventoryUtils";
import { isGoogleAnalytics } from "../../../util/vendors";

export interface VendorDataSellingSectionProps {
  /**
   * Prefix for RadioGroup `name` (and derived input ids). Must be unique per mounted instance so
   * radios in the inventory slideout and vendor details modal do not share one HTML radio group.
   */
  radioGroupNamePrefix: string;
  vendor: VendorInventoryItem;
  pageContext: CollectionContext;
  org: OrganizationDto;
  setIsDisabledWhileLoading: (value: boolean) => void;
  sellingAndSharingAnswers: SurveyAnswers;
  setGoogleAnalyticsDataSharingAnswer: (newAnswer: boolean) => Promise<void>;
  updateVendor: (v: UpdateVendorArgs) => Promise<void>;
  beforeGoogleAnalyticsQuestion?: React.ReactNode;
  className?: string;
}

export function VendorDataSellingSection({
  radioGroupNamePrefix,
  vendor,
  pageContext,
  org,
  setIsDisabledWhileLoading,
  sellingAndSharingAnswers,
  setGoogleAnalyticsDataSharingAnswer,
  updateVendor,
  beforeGoogleAnalyticsQuestion,
  className,
}: VendorDataSellingSectionProps) {
  const recipient = vendor.recipientData;
  if (!recipient) {
    return null;
  }

  const lockedSellingValues = isDataSellingLockedForVendor(recipient, sellingAndSharingAnswers);
  const finalSellingValue =
    lockedSellingValues.dataSelling !== null
      ? lockedSellingValues.dataSelling
      : recipient.ccpaIsSelling;
  const allowEditingSelling = lockedSellingValues.dataSelling === null;
  const isGoogleAnalyticsVendor = isGoogleAnalytics(recipient);
  const googleAnalyticsQuestionAnswered =
    hasGoogleAnalyticsDataSharingAnswer(sellingAndSharingAnswers);

  const lockedUploadValues = isUploadLockedForVendor(recipient);
  const finalUploadValue =
    lockedUploadValues.upload !== null ? lockedUploadValues.upload : recipient.isUploadVendor;
  const allowEditingUpload = lockedUploadValues.upload === null;

  const sellingRadioGroup = (
    <RadioGroup
      name={`${radioGroupNamePrefix}-isSelling`}
      direction="horizontal"
      options={[
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
      ]}
      selectedValue={finalSellingValue !== null ? (finalSellingValue ? "YES" : "NO") : undefined}
      locked={!allowEditingSelling}
      onChange={async (newValue: string) => {
        setIsDisabledWhileLoading(true);
        await updateVendor({
          vendorId: recipient.vendorId,
          vendorSettings: {
            ...recipient,
            ccpaIsSelling: newValue === "YES",
          },
        });
        setIsDisabledWhileLoading(false);
      }}
    />
  );

  const uploadRadioGroup = (
    <RadioGroup
      name={`${radioGroupNamePrefix}-isUploading`}
      direction="horizontal"
      options={[
        {
          label: "Yes",
          value: "YES",
        },
        {
          label: "No",
          value: "NO",
        },
      ]}
      selectedValue={finalUploadValue !== null ? (finalUploadValue ? "YES" : "NO") : undefined}
      locked={!allowEditingUpload}
      onChange={async (newValue: string) => {
        setIsDisabledWhileLoading(true);
        await updateVendor({
          vendorId: vendor.vendorId,
          vendorSettings: {
            ...recipient,
            isUploadVendor: newValue === "YES",
          },
        });
        setIsDisabledWhileLoading(false);
      }}
    />
  );

  return (
    <div className={clsx(className)}>
      {isGoogleAnalyticsVendor && (
        <>
          {beforeGoogleAnalyticsQuestion}
          <div className="flex justify-between items-start gap-[12px]">
            <div className="font-medium text-gray-700">
              Has your business turned off (or will it turn off) data sharing in Google Analytics?
            </div>
            <RadioGroup
              name={`${radioGroupNamePrefix}-googleAnalyticsDataSharing`}
              direction="horizontal"
              options={[
                { label: "Yes", value: "YES" },
                { label: "No", value: "NO" },
              ]}
              selectedValue={
                googleAnalyticsQuestionAnswered
                  ? sellingAndSharingAnswers["google-analytics-data-sharing"] === "true"
                    ? "YES"
                    : "NO"
                  : undefined
              }
              onChange={async (newValue: string) => {
                setIsDisabledWhileLoading(true);
                await setGoogleAnalyticsDataSharingAnswer(newValue === "YES");
                setIsDisabledWhileLoading(false);
              }}
            />
          </div>
        </>
      )}

      {shouldShowSelling(recipient, pageContext) &&
        (!isGoogleAnalyticsVendor || googleAnalyticsQuestionAnswered) && (
          <div className={clsx(isGoogleAnalyticsVendor && "mt-[24px]")}>
            <div className="flex justify-between items-start gap-[12px]">
              <div className="font-medium text-gray-700">
                {org.name} is selling personal information to {vendor.name}
              </div>
              {lockedSellingValues.dataSellingLockedTooltipMessage &&
              lockedSellingValues.dataSellingLockedTooltipMessage.length > 0 ? (
                <ToolTip text={lockedSellingValues.dataSellingLockedTooltipMessage} width="200px">
                  {sellingRadioGroup}
                </ToolTip>
              ) : (
                sellingRadioGroup
              )}
            </div>

            {finalSellingValue && (
              <div className="flex justify-between items-start gap-[12px] mt-[12px]">
                <div className="pt-[6px]">
                  <svg
                    width="12"
                    height="13"
                    viewBox="0 0 12 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.25 8.26172L6.75 12.7617L5.69531 11.707L8.40234 9H0V0H1.51172V7.52344H8.40234L5.69531 4.81641L6.75 3.76172L11.25 8.26172Z"
                      fill="black"
                    />
                  </svg>
                </div>
                <div>
                  <div className="text-gray-700 font-medium">
                    {org.name} uploads, transmits, or grants access to consumer data to{" "}
                    {vendor.name}
                  </div>
                  <div className="text-neutral-500">
                    Answer "no" if this vendor receives data only through automated means on your
                    website, such as a cookie
                  </div>
                </div>
                {lockedUploadValues.uploadLockedTooltipMessage &&
                lockedUploadValues.uploadLockedTooltipMessage.length > 0 ? (
                  <ToolTip text={lockedUploadValues.uploadLockedTooltipMessage} width="200px">
                    {uploadRadioGroup}
                  </ToolTip>
                ) : (
                  uploadRadioGroup
                )}
              </div>
            )}
          </div>
        )}
    </div>
  );
}
